import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import logo from '../../assets/images/logo.png';
import { Fragment } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useMatches } from "react-router-dom";
import {
    RootState,
    useAppDispatch,
    useAppSelector,
    logout,
    loadNotifications,
    loadUnreadNotificationCount,
    markAllNotificationsAsRead,
    clearAllNotifications,
    deleteNotification,
    markNotificationAsRead,
    updateUser,
    onRemoteUpdate,
} from '../../redux';
import type { UiLanguage } from '../../utils/uiPreferences';
import { Ability, ActiveTrack, Icon, QuickChangeModal, UserAvatar, Button } from '../../components';
import { Change, InAppNotification, User, Project} from '../../models';
import {Badge} from 'flowbite-react';
import type { IconType } from '../../components';
import {SearchModal} from "../../components/SearchModal";
import {
    createNotificationInboxChannel,
    parseNotificationInboxMessage,
} from '../../utils/notificationInboxSync';

function classNames(...classes : string[]) {
  return classes.filter(Boolean).join(' ')
}

function Topbar() {
    const { t, i18n }                       = useTranslation();
    const matches                           = useMatches();
    const [modalIsVisible, setModalVisible] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state : RootState) => state.auth);
    const { notifications, unreadCount, updating } = useAppSelector((state : RootState) => state.notifications);
    const isUpdatingNotifications = updating === 'pending';

    const persistUiLanguage = (language: UiLanguage) => {
        i18n.changeLanguage(language);
        if (!user?._id)
            return;
        dispatch(updateUser({
            _id: user._id,
            email: user.email,
            firstname: user.firstname,
            lastname: user.lastname,
            phone: user.phone,
            role: user.role,
            avatar: user.avatar,
            favoriteProjects: user.favoriteProjects,
            notificationPreferences: user.notificationPreferences,
            appearancePreferences: user.appearancePreferences,
            trackingPreferences: user.trackingPreferences,
            dashboardPreferences: user.dashboardPreferences,
            work_rate: user.work_rate,
            account_type: user.account_type,
            uiPreferences: {
                ...(user.uiPreferences || {}),
                language,
            },
        }));
    };

    // SearchBar
    const [isSearchModalVisible, setSearchModalVisible] = useState(false);
    const { setting } = useAppSelector((state: RootState) => state.settings);


    const navigation = React.useMemo(() => {
        const items = [{
            name: 'app.dashboard',
            href: '/',
            current: true,
            icon: 'home',
            ability: 'user:logout',
        }, {
            name: 'app.projects',
            href: '/projects',
            current: false,
            icon: 'project',
            ability: 'project:read',
        }, {
            name: 'app.goals',
            href: '/goals',
            current: false,
            icon: 'goal',
            ability: 'goal:read',
        }, {
            name: 'app.sprints',
            href: '/sprints',
            current: false,
            icon: 'sprint',
            ability: 'sprint:read',
        }, {
            name: 'app.triage',
            href: '/triage',
            current: false,
            icon: 'priority',
            ability: 'change:read',
        }, {
            name: 'app.changes',
            href: '/changes',
            current: false,
            icon: 'list',
            ability: 'change:read',
        }, {
            name: 'app.quotes',
            href: '/quotes',
            current: false,
            icon: 'law',
            ability: 'quote:read',
        }];

        if (setting?.outline?.enabled)
            items.push({
                name: 'app.documentations',
                href: '/documentations',
                current: false,
                icon: 'comment',
                ability: 'documentation:read',
            });

        if (setting?.metabase?.enabled)
            items.push({
                name: 'app.analysis',
                href: '/analysis',
                current: false,
                icon: 'chart',
                ability: 'analysis:read',
            });

        if (user?.role === User.ROLE_CLIENT)
            return items.filter(item => item.href !== '/triage');

        return items;
    }, [setting, user?.role]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const element = event.target as HTMLElement;
            if (['INPUT', 'TEXTAREA'].includes(element?.tagName))
                return;

            // consider Wysiwyg editors
            if (element?.tagName === 'DIV' && element.className.includes('DraftEditor'))
                return;

            const openSearch =
                (event.shiftKey && event.key === '/')
                || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k');

            if (openSearch) {
                event.preventDefault();
                setSearchModalVisible(true);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        if (!user?._id)
            return;

        dispatch(loadUnreadNotificationCount());
        dispatch(loadNotifications({ limit: 20 }));
    }, [dispatch, user?._id]);

    // Mirror Mercure inbox events from other tabs (SSE often stalls with many tabs).
    useEffect(() => {
        if (!user?._id)
            return;

        const channel = createNotificationInboxChannel();
        if (!channel)
            return;

        channel.onmessage = (event: MessageEvent) => {
            const message = parseNotificationInboxMessage(event.data);
            if (!message)
                return;
            dispatch(onRemoteUpdate(message.update));
        };

        return () => {
            channel.close();
        };
    }, [dispatch, user?._id]);

    // Safety net when an SSE tab was throttled while hidden.
    useEffect(() => {
        if (!user?._id)
            return;

        const refreshInbox = () => {
            if (document.visibilityState !== 'visible')
                return;
            dispatch(loadUnreadNotificationCount());
            dispatch(loadNotifications({ limit: 20 }));
        };

        document.addEventListener('visibilitychange', refreshInbox);
        return () => {
            document.removeEventListener('visibilitychange', refreshInbox);
        };
    }, [dispatch, user?._id]);

    const openNotification = (notification: InAppNotification) => {
        dispatch(markNotificationAsRead(notification));
        if (!notification.link)
            return;

        // Accept absolute URLs from older notifications and keep SPA navigation.
        try {
            const url = new URL(notification.link, window.location.origin);
            navigate(`${url.pathname}${url.search}${url.hash}`);
        } catch (err) {
            navigate(notification.link);
        }
    };

    return (
        <Disclosure as="nav" className="sticky top-0 z-50 bg-gray-800 print:hidden">
            {({ open }) => (
                <>
                    <div className="mx-auto px-2 md:px-6 lg:px-8">
                        <div className="relative flex h-11 items-center justify-between">
                            <div className="absolute inset-y-0 left-0 flex items-center md:hidden">
                                {/* Mobile menu button*/}
                                <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                                    <span className="sr-only">Open main menu</span>
                                    {open ? (
                                        <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                                    ) : (
                                        <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                                    )}
                                </Disclosure.Button>
                            </div>

                            <div className="flex flex-1 items-center justify-center md:items-stretch md:justify-start">
                                <div className="flex-shrink-0 hidden md:flex items-center">
                                    <img className=" h-6 w-auto" src={logo} alt="Yoda" />
                                </div>
                                <SearchModal
                                    visible={isSearchModalVisible}
                                    onClose={() => setSearchModalVisible(false)}
                                />
                                <div className="hidden md:ml-6 md:block">
                                    <div className="flex space-x-4">
                                        {navigation.map((item) => (
                                            <Ability key={item.name} can={item.ability}>
                                                <Link
                                                    to={item.href}
                                                    className={classNames(
                                                        item.href === (matches[1].pathname) ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                                        'rounded-md px-3 py-1 text-sm font-medium flex items-center'
                                                    )}
                                                >
                                                    <Icon type={item.icon as IconType} color="white"
                                                          className="inline mr-1" size={5}/>
                                                    <span className="hidden xl:block">
                                                        {t(item.name)}
                                                    </span>
                                                </Link>
                                            </Ability>
                                        ))}
                                        <span
                                            onClick={() => setSearchModalVisible(true)}
                                            className="cursor-pointer text-gray-300 hover:bg-gray-700 hover:text-white rounded-md px-3 py-1 text-sm font-medium flex items-center"
                                        >
                                            <Icon type="search" color="white" className="inline mr-1" size={4}/>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="absolute inset-y-0 right-0 flex items-center pr-2 md:static md:inset-auto md:ml-6 md:pr-0">
                                <Ability can="track:create">
                                    <div className="flex justify-center text-white mr-0 md:mr-3">
                                        <ActiveTrack />
                                    </div>
                                </Ability>
                                <div className="flex justify-center text-white">
                                    <Button
                                        className="block md:hidden"
                                        color="none"
                                        title=""
                                        icon="search"
                                        onClick={() => setSearchModalVisible(true)}
                                    />
                                </div>
                                <Ability can="change:create">
                                    <div className="flex justify-center text-white">
                                        <small>
                                            <Button
                                                small
                                                icon='plus'
                                                iconColor='white'
                                                iconSize={4}
                                                color='primary'
                                                title={''}
                                                textColor='white'
                                                onClick={() => setModalVisible(true)}
                                            />
                                        </small>
                                    </div>
                                </Ability>
                                {modalIsVisible && (
                                    <QuickChangeModal
                                        show={modalIsVisible}
                                        onClose={() => setModalVisible(false)}
                                        onAdded={(change: Change) => {
                                            setModalVisible(false);
                                            navigate(Project.path(change.project, 'change', change?._id));
                                        }}
                                    />
                                )}
                                <Menu as="div" className="relative ml-3">
                                    <Menu.Button
                                        className="relative flex rounded-full bg-gray-800 p-1 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                                        title={t('notifications.title')}
                                    >
                                        <span className="sr-only">{t('notifications.title')}</span>
                                        <Icon type="notification" color="white" size={5} />
                                        {unreadCount > 0 && (
                                            <span className="absolute -right-1 -top-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                                                {unreadCount > 9 ? '9+' : unreadCount}
                                            </span>
                                        )}
                                    </Menu.Button>
                                    <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                    >
                                        <Menu.Items className="absolute right-0 z-10 mt-2 w-80 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none flex max-h-[min(24rem,calc(100vh-5rem))] flex-col overflow-hidden">
                                            <div className="flex shrink-0 items-center justify-between border-b dark:border-gray-700 px-3 py-2">
                                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {t('notifications.title')}
                                                </span>
                                                {notifications.length > 0 && (
                                                    <div className="flex items-center gap-2">
                                                        {isUpdatingNotifications && (
                                                            <Icon type="loading" size={4} color="gray-400" />
                                                        )}
                                                        {unreadCount > 0 && (
                                                            <button
                                                                className="text-xs text-blue-700 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                disabled={isUpdatingNotifications}
                                                                onClick={() => dispatch(markAllNotificationsAsRead())}
                                                            >
                                                                {t('notifications.mark_all_read')}
                                                            </button>
                                                        )}
                                                        <button
                                                            className="text-xs text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            disabled={isUpdatingNotifications}
                                                            onClick={() => dispatch(clearAllNotifications())}
                                                        >
                                                            {t('notifications.clear_all')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-h-0 flex-1 overflow-y-auto">
                                                {notifications.length === 0 && (
                                                    <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                                        {t('notifications.empty')}
                                                    </div>
                                                )}
                                                {notifications.map(notification => (
                                                    <Menu.Item key={notification._id}>
                                                        {({ active }) => (
                                                            <div
                                                                className={classNames(
                                                                    active ? 'bg-gray-100 dark:bg-gray-700' : '',
                                                                    'flex items-start gap-1 px-2 py-1'
                                                                )}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openNotification(notification)}
                                                                    className={classNames(
                                                                        !notification.readAt ? 'font-semibold' : '',
                                                                        'min-w-0 flex-1 text-left block px-2 py-1 text-sm text-gray-700 dark:text-gray-200'
                                                                    )}
                                                                >
                                                                    <span className="block truncate">
                                                                        {notification.title}
                                                                    </span>
                                                                    {notification.body && (
                                                                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                                                            {notification.body}
                                                                        </span>
                                                                    )}
                                                                    {notification.createdAt && (
                                                                        <span className="block text-xs text-gray-400 dark:text-gray-500">
                                                                            {notification.createdAt.fromNow()}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title={t('notifications.delete')}
                                                                    aria-label={t('notifications.delete')}
                                                                    disabled={isUpdatingNotifications}
                                                                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-600 dark:hover:text-gray-100"
                                                                    onClick={(event) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        dispatch(deleteNotification(notification));
                                                                    }}
                                                                >
                                                                    <Icon type="close" size={4} color="current" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </Menu.Item>
                                                ))}
                                            </div>
                                        </Menu.Items>
                                    </Transition>
                                </Menu>
                                {/* Profile dropdown */}
                                {user && (
                                <Menu as="div" className="relative ml-3">
                                    <div>
                                        <Menu.Button className="flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                                            <span className="sr-only">Open user menu</span>
                                            <UserAvatar user={user} noTooltip />
                                        </Menu.Button>
                                    </div>
                                    <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                    >
                                        <Menu.Items className="absolute right-0 z-10 mt-2 w-52 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y dark:divide-gray-700">
                                            <div className="bg-blue-9 rounded-b-md">
                                                <div className="flex justify-center text-white mb-2 px-10 pt-1 py-0 -space-x-1.5">
                                                    <Badge
                                                        className="px-2 cursor-pointer w-full"
                                                        onClick={() => persistUiLanguage('fr')}
                                                        color={i18n.language === 'fr' ? 'dark' : 'light'}
                                                    >
                                                        FR&nbsp;
                                                    </Badge>
                                                    <Badge
                                                        className="px-2 cursor-pointer w-full"
                                                        onClick={() => persistUiLanguage('en')}
                                                        color={i18n.language === 'en' ? 'dark' : 'light'}
                                                    >
                                                        EN
                                                    </Badge>
                                                    <Badge
                                                        className="px-2 cursor-pointer w-full"
                                                        onClick={() => persistUiLanguage('de')}
                                                        color={i18n.language === 'de' ? 'dark' : 'light'}
                                                    >
                                                        DE
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div>
                                                <Ability can="user:logout">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <Link
                                                                to={`/users/edit/${user._id}`}
                                                                className={classNames(
                                                                    (active ? 'bg-gray-100 dark:bg-gray-700' : ''),
                                                                    'w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                                                                )}
                                                            >
                                                                <Icon type="user" className="inline mr-1" />
                                                                { t('app.user_profile') }
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                </Ability>
                                                <Ability can="track:create">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <Link
                                                                to={`/users/tracks`}
                                                                className={classNames(
                                                                    (active ? 'bg-gray-100 dark:bg-gray-700' : ''),
                                                                    'w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                                                                )}
                                                            >
                                                                <Icon type="timer" className="inline mr-1" />
                                                                { t('app.user_tracks') }
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                </Ability>
                                                <Ability can="log:read">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <Link
                                                                to={`/users/activity`}
                                                                className={classNames(
                                                                    (active ? 'bg-gray-100 dark:bg-gray-700' : ''),
                                                                    'w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                                                                )}
                                                            >
                                                                <Icon type="activity" className="inline mr-1" />
                                                                { t('app.user_activity') }
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                </Ability>
                                            </div>
                                            <div>
                                                <Ability can="user:read">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <Link
                                                                to={'/users'}
                                                                className={classNames(
                                                                    (active ? 'bg-gray-100 dark:bg-gray-700' : ''),
                                                                    'w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                                                                )}
                                                            >
                                                                <Icon type="settings" className="inline mr-1" />
                                                                { t('app.users') }
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                </Ability>
                                                <Ability can="client:read">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <Link
                                                                to={`/clients`}
                                                                className={classNames(
                                                                    (active ? 'bg-gray-100 dark:bg-gray-700' : ''),
                                                                    'w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                                                                )}
                                                            >
                                                                <Icon type="client" className="inline mr-1" />
                                                                { t('app.clients') }
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                </Ability>
                                            </div>
                                            <div>
                                                <Ability can="setting:read">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <Link
                                                                to={'/settings/edit/default'}
                                                                className={classNames(
                                                                    (active ? 'bg-gray-100 dark:bg-gray-700' : ''),
                                                                    'w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                                                                )}
                                                            >
                                                                <Icon type="setting" className="inline mr-1" />
                                                                { t('app.settings') }
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                </Ability>
                                                {user?.role !== User.ROLE_CLIENT
                                                    && setting?.rt?.enabled
                                                    && setting?.rt?.base_url
                                                    && (
                                                    <Ability can="change:create">
                                                        <Menu.Item>
                                                            {({ active }) => (
                                                                <Link
                                                                    to={'/rt-import'}
                                                                    className={classNames(
                                                                        (active ? 'bg-gray-100 dark:bg-gray-700' : ''),
                                                                        'w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-200'
                                                                    )}
                                                                >
                                                                    <Icon type="comment" className="inline mr-1" />
                                                                    { t('app.rt_import') }
                                                                </Link>
                                                            )}
                                                        </Menu.Item>
                                                    </Ability>
                                                )}
                                            </div>
                                            <Ability can="user:logout">
                                                <div>
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <button onClick={() => dispatch(logout())}
                                                                className={classNames(active ? 'bg-gray-100 dark:bg-gray-700' : '', 'w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-200')}
                                                            >
                                                                <Icon type="logout" className="inline mr-1" />
                                                                { t('app.logout') }
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                </div>
                                            </Ability>
                                        </Menu.Items>
                                    </Transition>
                                </Menu>
                                )}
                            </div>
                        </div>
                    </div>

                    <Disclosure.Panel className="md:hidden">
                        <div className="space-y-1 px-2 pb-3 pt-2">
                            {navigation.map((item) => (
                                <Ability key={item.name} can={item.ability}>
                                    <Disclosure.Button
                                        as="a"
                                        href={item.href}
                                        className={classNames(
                                            item.current ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                            'block rounded-md px-3 py-2 text-base font-medium'
                                        )}
                                        aria-current={item.current ? 'page' : undefined}
                                    >
                                        <Icon type={item.icon as IconType} color="white" className="inline mr-1" size={5} />
                                        { t(item.name) }
                                    </Disclosure.Button>
                                </Ability>
                            ))}
                        </div>
                    </Disclosure.Panel>
                </>
            )}
        </Disclosure>
    );
}

export default Topbar;
